const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const whole = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 })

export const CURRENCIES = [
  { code: "EUR", symbol: "€", scale: 1 },
  { code: "USD", symbol: "$", scale: 1 },
  { code: "GBP", symbol: "£", scale: 1 },
  { code: "JPY", symbol: "¥", scale: 100 }
]

export function currencyFromQuery() {
  const code = new URLSearchParams(location.search).get("currency")
  return CURRENCIES.find((currency) => currency.code === code) || CURRENCIES[0]
}

export function nextCurrency(currency) {
  return CURRENCIES[(CURRENCIES.indexOf(currency) + 1) % CURRENCIES.length]
}

export function money(value, currency) {
  return `${value < 0 ? "-" : ""}${currency.symbol}${whole.format(Math.abs(Math.round(value)))}`
}

export function compact(value, currency) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.code, notation: "compact", maximumFractionDigits: 1 }).format(value)
}

export function percent(value) {
  return `${Number((value * 100).toFixed(1))}%`
}

export function plural(count, word) {
  return `${count} ${word}${count === 1 ? "" : "s"}`
}

export function duration(months) {
  const years = Math.floor(months / 12)
  const rest = months % 12
  return [years && plural(years, "year"), rest && plural(rest, "month")].filter(Boolean).join(" ")
}

export function shortDuration(months) {
  const years = Math.floor(months / 12)
  const rest = months % 12
  return [years && `${years}y`, rest && `${rest}m`].filter(Boolean).join(" ")
}

export function monthYear(offset, short = false) {
  const date = new Date()
  date.setDate(1)
  date.setMonth(date.getMonth() + offset)
  const name = MONTHS[date.getMonth()]
  return `${short ? name.slice(0, 3) : name} ${date.getFullYear()}`
}

export function change(base, other) {
  if (base === null && other === null) return { text: "Still out of reach", tone: "fire-lever__change--none" }
  if (other === null) return { text: "Out of reach", tone: "calculator-down" }
  if (base === null) return { text: `Reached in ${duration(other)}`, tone: "calculator-up" }

  const difference = base - other
  if (difference === 0) return { text: "No change", tone: "fire-lever__change--none" }
  return { text: `${duration(Math.abs(difference))} ${difference > 0 ? "sooner" : "later"}`, tone: difference > 0 ? "calculator-up" : "calculator-down" }
}

export function saveQuery(form, currency) {
  const query = new URLSearchParams(new FormData(form))
  if (currency !== CURRENCIES[0]) query.set("currency", currency.code)
  history.replaceState(history.state, "", `${location.pathname}?${query}`)
  document.querySelectorAll(".fire-guide__link").forEach((link) => { link.search = query.toString() })
}

export function line(label, values, color, options = {}) {
  return { label, data: values.map(Math.round), borderColor: color, borderWidth: 2, fill: false, pointRadius: 0, pointHoverRadius: 0, lineTension: 0, ...options }
}

export function drawChart(chart, canvas, { labels, datasets, horizon, currency }) {
  if (chart) {
    chart.data.labels = labels
    chart.data.datasets = datasets
    chart.options.scales.yAxes[0].ticks.callback = (value) => compact(value, currency)
    chart.options.tooltips.callbacks.label = (item, data) => `${data.datasets[item.datasetIndex].label}: ${money(item.yLabel, currency)}`
    chart.update()
    return chart
  }

  return new window.Chart(canvas.getContext("2d"), {
    type: "line",
    data: { labels, datasets },
    options: {
      maintainAspectRatio: false,
      responsive: true,
      animation: false,
      legend: { display: false },
      hover: { mode: "index", intersect: false },
      tooltips: {
        mode: "index",
        intersect: false,
        backgroundColor: "#fff",
        titleFontColor: "#333",
        bodyFontColor: "#666",
        bodySpacing: 4,
        xPadding: 12,
        callbacks: {
          label: (item, data) => `${data.datasets[item.datasetIndex].label}: ${money(item.yLabel, currency)}`
        }
      },
      scales: {
        yAxes: [{
          gridLines: { drawBorder: false, color: "rgba(255, 255, 255, 0.06)", zeroLineColor: "rgba(255, 255, 255, 0.06)" },
          ticks: { beginAtZero: true, maxTicksLimit: 5, padding: 8, fontColor: "#9a9a9a", callback: (value) => compact(value, currency) }
        }],
        xAxes: [{
          gridLines: { display: false, drawBorder: false },
          ticks: { maxTicksLimit: Math.min(8, horizon), maxRotation: 0, padding: 8, fontColor: "#9a9a9a" }
        }]
      }
    }
  })
}

export function yearLabels(points) {
  const start = new Date().getFullYear()
  return points.filter((point) => point.month % 12 === 0).map((point) => String(start + point.month / 12))
}

export function renderSteps(container, template, rows, currency) {
  container.replaceChildren(...rows.map((row) => {
    const element = template.content.firstElementChild.cloneNode(true)
    element.classList.add(`fire-step--${row.kind}`)
    element.querySelector(".fire-step__when").textContent = row.when || monthYear(row.month, true)
    element.querySelector(".fire-step__title").textContent = row.title
    element.querySelector(".fire-step__detail").textContent = row.detail
    element.querySelector(".fire-step__amount").textContent = money(row.amount, currency)
    return element
  }))
}

export function renderLevers(container, template, base, levers) {
  container.replaceChildren(...levers.map((lever) => {
    const element = template.content.firstElementChild.cloneNode(true)
    const outcome = change(base, lever.months)
    const result = element.querySelector(".fire-lever__change")
    element.querySelector(".fire-lever__label").textContent = lever.label
    element.querySelector(".fire-lever__detail").textContent = lever.detail
    result.textContent = lever.text || outcome.text
    result.classList.add(lever.tone || outcome.tone)
    return element
  }))
}
